/**
 * Workspace lateral do problema — painel de governança e resolução.
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, ChevronRight, ExternalLink, AlertTriangle, Clock, User,
  LinkIcon, Crosshair, Shield, Activity,
} from 'lucide-react';
import { EnterpriseBadge } from '../layout/EnterpriseTable';
import { useOrgMembers } from '../../hooks/useOrgMembers';
import { resolveIssueAssigneeName } from '../../utils/issueAssignee';
import { getIssueNextAction } from '../../utils/issueNextAction';
import { enrichIssue } from '../../services/problemDashboardData';
import {
  ISSUE_STATUS_LABELS, ISSUE_SEVERITY_LABELS, ISSUE_TYPE_LABELS, ISSUE_MODE_LABELS,
} from '../../types/issue';
import type { Issue } from '../../types/issue';
import { stageVisual, workspaceStages } from '../../utils/issueProgress';
import { OperationalOriginBanner } from './OperationalOriginBanner';

interface ProblemSummaryPanelProps {
  issue: Issue;
  organizationId: string;
  onClose: () => void;
  onRefresh: () => void;
}

const SEV: Record<string, string> = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-amber-500 text-white',
  low: 'bg-slate-500 text-white',
};

const STA: Record<string, string> = {
  open: 'bg-red-500/10 text-red-600',
  investigation: 'bg-amber-500/10 text-amber-600',
  root_cause: 'bg-orange-500/10 text-orange-600',
  action_plan: 'bg-blue-500/10 text-blue-600',
  execution: 'bg-indigo-500/10 text-indigo-600',
  validation: 'bg-purple-500/10 text-purple-600',
  monitoring: 'bg-cyan-500/10 text-cyan-600',
  closed: 'bg-emerald-500/10 text-emerald-600',
};

export const ProblemSummaryPanel: React.FC<ProblemSummaryPanelProps> = ({ issue, organizationId, onClose }) => {
  const navigate = useNavigate();
  const { members } = useOrgMembers(organizationId);
  const enriched = useMemo(() => enrichIssue(issue), [issue]);
  const nextAction = useMemo(() => getIssueNextAction(issue), [issue]);
  const stages = workspaceStages(issue);
  const activeStageIdx = stages.findIndex(stage => stage.statuses.includes(issue.status));

  const assignee = issue.assigned_to ? resolveIssueAssigneeName(issue.assigned_to, members) : null;
  const slaLabel = issue.status === 'closed' || issue.status === 'cancelled'
    ? '—'
    : enriched.flags.slaOverdue
      ? `${Math.abs(enriched.slaRemainingDays)}d além do prazo`
      : `${enriched.slaRemainingDays}d restantes`;
  const openActions = issue.linked_actions_count ?? 0;
  const goalId = issue.metadata?.linked_hoshin_goal_id;

  const alerts: { icon: React.ReactNode; text: string }[] = [];
  if (enriched.flags.slaOverdue) alerts.push({ icon: <Clock size={11} />, text: 'Prazo de resposta vencido' });
  if (enriched.flags.unassigned) alerts.push({ icon: <User size={11} />, text: 'Sem responsável' });
  if (openActions === 0 && issue.status !== 'closed') alerts.push({ icon: <LinkIcon size={11} />, text: 'Sem ações vinculadas' });
  if (enriched.flags.critical) alerts.push({ icon: <AlertTriangle size={11} />, text: 'Criticidade máxima' });

  return (
    <aside className="flex w-[380px] shrink-0 flex-col border-l border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <Activity size={13} className="text-blue-600" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Workspace do Caso</span>
        </div>
        <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
          <X size={14} />
        </button>
      </div>

      <OperationalOriginBanner
        metadata={issue.metadata}
        source={issue.source}
        projectId={issue.project_id}
        organizationId={issue.organization_id}
      />

      {/* Severity strip */}
      <div className={`px-3 py-2 text-center text-[10px] font-bold uppercase tracking-wide ${SEV[issue.severity]}`}>
        {ISSUE_SEVERITY_LABELS[issue.severity]}
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-1 border-b border-red-100 bg-red-50 px-3 py-2 dark:border-red-900/30 dark:bg-red-950/20">
          {alerts.map(a => (
            <div key={a.text} className="flex items-center gap-1.5 text-[11px] font-semibold text-red-700 dark:text-red-400">
              {a.icon} {a.text}
            </div>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {/* Title & badges */}
        <div className="border-b border-slate-100 px-3 py-3 dark:border-slate-800">
          <h2 className="text-[13px] font-semibold leading-snug text-slate-900 dark:text-slate-100">{issue.title}</h2>
          <div className="mt-2 flex flex-wrap gap-1">
            <EnterpriseBadge label={ISSUE_STATUS_LABELS[issue.status]} color={STA[issue.status]} />
            <EnterpriseBadge label={ISSUE_TYPE_LABELS[issue.type]} color="bg-slate-100 text-slate-600 dark:bg-slate-800" />
            {issue.mode === 'structured_problem' && (
              <EnterpriseBadge label={ISSUE_MODE_LABELS[issue.mode]} color="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" />
            )}
          </div>
        </div>

        {/* Stage progress mini */}
        <div className="border-b border-slate-100 px-3 py-3 dark:border-slate-800">
          <p className="mb-2 text-[9px] font-bold uppercase tracking-wide text-slate-400">Etapa da resolução</p>
          <div className="flex items-center gap-0.5 overflow-x-auto">
            {stages.map((stage, idx) => {
              const visual = stageVisual(issue, [], stage.key, idx, activeStageIdx);
              return (
                <React.Fragment key={stage.key}>
                  <div className="flex shrink-0 flex-col items-center">
                    <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-full text-[7px] font-bold ${
                      visual === 'complete'
                        ? 'bg-emerald-600 text-white'
                        : visual === 'current'
                          ? 'bg-blue-600 text-white'
                          : visual === 'traversed'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-slate-200 text-slate-400 dark:bg-slate-700'
                    }`}>
                      {visual === 'complete' ? '✓' : idx + 1}
                    </span>
                    <span className={`mt-0.5 max-w-[48px] truncate text-center text-[8px] ${visual === 'current' ? 'font-bold text-blue-600' : 'text-slate-400'}`}>
                      {stage.label}
                    </span>
                  </div>
                  {idx < stages.length - 1 && (
                    <div className={`mb-3 h-px w-2 shrink-0 ${visual === 'complete' ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {activeStageIdx < 0 && issue.status !== 'closed' && (
            <p className="mt-2 text-[11px] text-amber-700">Fluxo legado · {ISSUE_STATUS_LABELS[issue.status]}</p>
          )}
        </div>

        {/* Governance metrics */}
        <div className="border-b border-slate-100 px-3 py-3 dark:border-slate-800">
          <p className="mb-2 text-[9px] font-bold uppercase tracking-wide text-slate-400">Governança</p>
          <div className="grid grid-cols-2 gap-2">
            <MetricBox icon={<User size={12} />} label="Responsável" value={assignee ?? 'Não atribuído'} alert={enriched.flags.unassigned} />
            <MetricBox icon={<Clock size={12} />} label="Prazo" value={slaLabel} alert={enriched.flags.slaOverdue} />
            <MetricBox icon={<Clock size={12} />} label="Idade" value={`${enriched.daysOpen}d`} />
            <MetricBox icon={<LinkIcon size={12} />} label="Ações" value={String(openActions)} alert={openActions === 0 && issue.status !== 'closed'} />
          </div>
        </div>

        {/* Links */}
        <div className="border-b border-slate-100 px-3 py-3 dark:border-slate-800">
          <p className="mb-2 text-[9px] font-bold uppercase tracking-wide text-slate-400">Rastreabilidade</p>
          <div className="space-y-1.5">
            <LinkRow icon={<Crosshair size={11} />} label="Projeto" value={issue.project_id ? 'Vinculado' : '—'} linked={!!issue.project_id} />
            <LinkRow icon={<Shield size={11} />} label="Workflow" value={issue.workflow_id ? 'Vinculado' : '—'} linked={!!issue.workflow_id} />
            <LinkRow icon={<Activity size={11} />} label="Meta Hoshin" value={goalId ? 'Vinculada' : '—'} linked={!!goalId} />
            {issue.department && (
              <LinkRow icon={<User size={11} />} label="Departamento" value={issue.department} linked />
            )}
          </div>
        </div>

        {/* Next action */}
        <div className="px-3 py-3">
          <div className="rounded border border-blue-200 bg-blue-50/80 px-3 py-2.5 dark:border-blue-900/40 dark:bg-blue-950/30">
            <p className="text-[9px] font-bold uppercase tracking-wide text-blue-600">Próxima ação obrigatória</p>
            <p className="mt-1 text-[12px] font-semibold leading-snug text-blue-900 dark:text-blue-300">{nextAction.label}</p>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="space-y-1.5 border-t border-slate-200 p-3 dark:border-slate-700">
        <button
          type="button"
          onClick={() => navigate(`/problemas/${issue.id}`)}
          className="flex w-full items-center justify-center gap-1.5 rounded bg-blue-600 px-3 py-2 text-[12px] font-semibold text-white hover:bg-blue-700"
        >
          Abrir Workspace Completo <ChevronRight size={14} />
        </button>
        <button
          type="button"
          onClick={() => navigate(`/problemas/${issue.id}`)}
          className="flex w-full items-center justify-center gap-1 text-[10px] text-slate-400 hover:text-slate-600"
        >
          <ExternalLink size={10} /> Investigação estruturada
        </button>
      </div>
    </aside>
  );
};

function MetricBox({
  icon, label, value, alert,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  alert?: boolean;
}) {
  return (
    <div className={`rounded border px-2 py-1.5 ${alert ? 'border-red-200 bg-red-50/50 dark:border-red-900/40 dark:bg-red-950/20' : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/50'}`}>
      <div className="flex items-center gap-1 text-[9px] text-slate-400">
        {icon} {label}
      </div>
      <p className={`mt-0.5 truncate text-[11px] font-semibold ${alert ? 'text-red-700 dark:text-red-400' : 'text-slate-800 dark:text-slate-200'}`}>
        {value}
      </p>
    </div>
  );
}

function LinkRow({
  icon, label, value, linked,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  linked?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className="text-slate-400">{icon}</span>
      <span className="w-20 shrink-0 text-slate-400">{label}</span>
      <span className={`truncate font-medium ${linked ? 'text-slate-800 dark:text-slate-200' : 'text-slate-400'}`}>{value}</span>
    </div>
  );
}

export default ProblemSummaryPanel;
